import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
// eslint-disable-next-line import/no-internal-modules
import type { ContactRole } from './__generated__/use-scheduled-event';

interface GetScheduledContactsQuery {
    getContactRoles: ContactRole[];
}

interface GetScheduledContactsQueryVariables {
    input: {
        location_id?: string;
        onsite_technical?: boolean;
        client_id?: number;
    };
}

const QUERY = gql`
query Contacts($input: GetContactRolesInput!) {
  getContactRoles(input: $input) {
    location {
      id
      name
    }
    user {
      id
      first_name
      last_name
      phone_number_area_code
      phone_number
      landline
      landline_extension
      landline_area_code
      email
      preferred_contact_method
      additional_information
    }
    onsite_technical
    onsite_technical_priority
  }
}
`;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useContacts = (locationId?: string, clientId?: string) => {
  const { data, loading } = useQuery<
    GetScheduledContactsQuery,
    GetScheduledContactsQueryVariables
  >(QUERY, {
    variables: {
        input: {
            location_id: locationId,
            onsite_technical: true,
            client_id: Number(clientId)
        }
    },
    skip: !locationId || !clientId
  });

  // sort by priority
  const contacts = useMemo(() => {
    if (!data?.getContactRoles){
        return data?.getContactRoles;
    }

    return [...data.getContactRoles]
    .sort((a, b) => (a.onsite_technical_priority ?? 0) - (b.onsite_technical_priority ?? 0));
  }, [data?.getContactRoles]);

  return {
    contacts,
    loading
  };
};

export interface AccountContact {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  preferred_contact_method?: string | null;
  enterprise_subuser?: boolean | null;
}

interface GetAccountContactsQuery {
  getContactRoles: { user?: AccountContact | null; onsite_technical_priority?: number | null }[];
}

/*
 * The account-wide branch of getContactRoles. The resolver picks its path from the input: give it a
 * location_id and it filters strictly by that location, dropping the roles held across the whole
 * account. So this one deliberately sends only client_id — that branch also brings in the account
 * holder, who is not a sub-user and never comes back from the location path.
 */
const ACCOUNT_QUERY = gql`
query AccountContacts($input: GetContactRolesInput!) {
  getContactRoles(input: $input) {
    onsite_technical_priority
    user {
      id
      first_name
      last_name
      email
      phone_number
      preferred_contact_method
      enterprise_subuser
    }
  }
}
`;


/*
 * The people who can be named as the requester of a ticket: the on-site technical contacts for the
 * location the event runs at, and only those. An empty picker here is the honest answer — it says
 * nobody is registered for this location, which is itself worth seeing.
 *
 * There was an account-wide fallback below, deliberately switched off: the resolver takes one path
 * or the other (pass a location_id and client_id is ignored), and the people it returned hold the
 * role at some other location, which is a hint rather than the contact. Restoring it means putting
 * the clientId parameter back too.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useRequesterContacts = (locationId?: string) => {
  const atLocation = useQuery<GetAccountContactsQuery, GetScheduledContactsQueryVariables>(
    ACCOUNT_QUERY,
    {
      variables: { input: { location_id: locationId, onsite_technical: true } },
      skip: !locationId
    }
  );

  const roles = atLocation.data?.getContactRoles;

  /*
   * The fallback, switched off:
   *
   * const onAccount = useQuery<GetAccountContactsQuery, GetScheduledContactsQueryVariables>(
   *   ACCOUNT_QUERY,
   *   {
   *     variables: { input: { client_id: Number(clientId), onsite_technical: true } },
   *     skip: !clientId || atLocation.loading || (roles?.length ?? 0) > 0
   *   }
   * );
   */

  const contacts = useMemo(() => {
    const byEmail = new Map<string, AccountContact & { priority: number }>();

    for (const role of roles ?? []) {
      const { user } = role;
      const email = user?.email ?? '';

      /*
       * The enterprise sub-user is a placeholder account with a generated address. It would pass
       * an "it has an email" test and then file the ticket against a mailbox nobody reads.
       */
      if (!user || !email || user.enterprise_subuser === true) {
        continue;
      }

      const priority = role.onsite_technical_priority ?? Number.MAX_SAFE_INTEGER;
      const seen = byEmail.get(email);

      if (!seen || priority < seen.priority) {
        byEmail.set(email, { ...user, priority });
      }
    }

    return [...byEmail.values()].sort((a, b) => a.priority - b.priority);
  }, [roles]);

  return { contacts, loading: atLocation.loading };
};
